import { createContext, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { API_CALLS } from "../../../../API/api_calls";
import { useContainerSize } from "../../../../hooks/useContainerSize";
import { actions, useAppDispatch, useAppSelector } from "../../../../redux";
import { CircleProps } from "../../../../redux/slices/circles/circles";
import { Modal } from "../../../Common";
import { FlexColumn } from "../../../Common/Styles";
import { CenterPanelProps } from "./CenterPanel";
import { DefaultPost } from "./DefaultPost";
import NewsFeedFilter from "./NewsFeedFilter";
import { OriginalPost } from "./OriginalPost";
import PostAnalysisModal from "./PostAnalysisModal";
import { PostCard } from "./PostCard";
import { PostCardContent } from "./PostCard/PostCardContent";

export const CenterPanelContext = createContext({} as CenterPanelProps.Context);

export const CenterPanel = () => {
  const dispatch = useAppDispatch();
  const [scrollPosition, setScrollPosition] = useState(0);
  const [selectedNewsFeedFilter, setSelectedNewsFeedFilter] = useState({
    label: "All",
    value: "all",
  });
  const [selectedCircle, setSelectedCircle] = useState<CircleProps.Circle[]>(
    []
  );
  const [showOriginalPost, setShowOriginalPost] = useState(false);
  const [originalPostId, setOriginalPostId] = useState<string>("");

  const [showExpandedPostModal, setShowExpandedPostModal] = useState(false);
  const [expandedPostId, setExpandedPostId] = useState("");

  const { remaining_posts, page, getNewsFeedPost_API_STATUS, page_size } =
    useAppSelector((state) => state.newsfeed);

  const originalPostRef = useRef<HTMLDivElement>(null);
  const { height: originalPostHeight } = useContainerSize(originalPostRef);

  useEffect(() => {
    if (page === 0) {
      dispatch(actions.newsfeed.setPage(1));
      return;
    }
    API_CALLS.NEWSFEED.getNewsFeedPosts({
      page,
      page_size,
      sort_by: selectedNewsFeedFilter.value,
      circle_id: selectedCircle.map((c) => c.id).join(","),
    });
  }, [page, page_size, selectedNewsFeedFilter, selectedCircle, dispatch]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          getNewsFeedPost_API_STATUS !== "progress" &&
            remaining_posts > 0 &&
            dispatch(actions.newsfeed.setPage(page + 1));
        }
      },
      { threshold: 0 }
    );

    const newsFeedListing = document.getElementById("newsfeedlisting");
    if (newsFeedListing) {
      const children = newsFeedListing.children;
      const childrenCount = children.length;
      if (!childrenCount) return;

      const targetElement = children[childrenCount - 2];
      if (targetElement instanceof Element) {
        observer.observe(targetElement);
      }
    }
    return () => observer.disconnect();
  }, [getNewsFeedPost_API_STATUS, page, dispatch, remaining_posts]);

  const originalPostClicked = (postId: string) => {
    setExpandedPostId(postId);
    setShowExpandedPostModal(true);
  };
  const originalPostOfRepostClicked = (postId: string) => {
    setScrollPosition(window.scrollY);
    setOriginalPostId(postId);
    setShowOriginalPost(true);
  };
  const onOriginalPostBackButtonClicked = () => {
    setShowOriginalPost(false);
    setOriginalPostId("");
    setTimeout(() => window.scrollTo(0, scrollPosition), 0);
  };

  return (
    <CenterPanelContext.Provider
      value={{
        originalPostId,
        showOriginalPost,
        originalPostOfRepostClicked,
        onOriginalPostBackButtonClicked,
        originalPostRef,
        expandedPostId,
        originalPostClicked,
        selectedCircle,
        setSelectedCircle,
      }}
    >
      <Container id="center-panel-container">
        {showExpandedPostModal && (
          <Modal
            from="expanded-post-card"
            type="Pages"
            setCloseButton={setShowExpandedPostModal}
            showCloseButton={false}
            CloseButtonPosition="top-right"
            closeModalOnClickOutside={showExpandedPostModal}
            maxWidth={950}
            stretchToFullWidth={false}
            stretchToFullHeight={true}
          >
            <PostCardContent
              postId={expandedPostId}
              isOnExpandedPostView={true}
            />
          </Modal>
        )}
        <OriginalPost />
        <DefaultView
          $originalPostHeight={originalPostHeight}
          $showOriginalPost={showOriginalPost}
          $gapInPx={8}
        >
          <DefaultPost />
          <NewsFeedFilter
            selectedNewsFeedFilter={selectedNewsFeedFilter}
            setSelectedNewsFeedFilter={setSelectedNewsFeedFilter}
          />
          <PostCard />
          <PostAnalysisModal />
        </DefaultView>
      </Container>
    </CenterPanelContext.Provider>
  );
};

const Container = styled.div`
  flex: 1;
  position: relative;
  border-radius: 10px;
`;
const DefaultView = styled(FlexColumn)<{
  $originalPostHeight: number;
  $showOriginalPost: boolean;
}>(
  ({ $originalPostHeight, $showOriginalPost }) =>
    `max-height: ${$showOriginalPost ? `${$originalPostHeight}px` : "unset"};
  overflow: ${$showOriginalPost ? "hidden" : "unset"};
  border-radius: 8px;`
);
